import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";
import { api } from "@/api/client";
import { AmbientBackground } from "@/components/AmbientBackground";

export default function ForgotPassword() {
  const { selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const theme = getTheme(selectedBusinessId);
  const [step, setStep] = useState("request"); // "request" | "reset"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await api.forgotPassword(email.trim());
      toast.success(res.message || "If an account exists, a reset code has been sent.");
      setStep("reset");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await api.resetPassword(email.trim(), code.trim(), password);
      toast.success(res.message || "Password updated. Please sign in.");
      nav("/sign-in");
    } catch (err) {
      toast.error(err.message || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <AmbientBackground />
      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between p-12 hero-gradient">
          <Link to="/" className="font-display text-2xl" style={{ color: "var(--hero-fg)" }}>
            {theme.name}
          </Link>
          <div>
            <h2 className="font-display text-4xl leading-tight" style={{ color: "var(--hero-fg)" }}>
              Forgot your password?
            </h2>
            <p className="mt-3 max-w-sm" style={{ color: "color-mix(in srgb, var(--hero-fg) 75%, transparent)" }}>
              We&apos;ll email you a 6-digit code to set a new one — no need to remember the old.
            </p>
          </div>
          <div className="text-xs" style={{ color: "color-mix(in srgb, var(--hero-fg) 60%, transparent)" }}>
            {theme.kind} · Order online
          </div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-12">
          {step === "request" ? (
            <form onSubmit={requestCode} className="w-full max-w-sm" data-testid="forgot-request-form">
              <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Reset password</h1>
              <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
                Enter your account email and we&apos;ll send a reset code.
              </p>
              <label className="block mt-6 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                Email
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none"
                  style={inputStyle}
                  placeholder="you@email.com"
                />
              </label>
              <button type="submit" disabled={loading} data-testid="forgot-send-btn" className="btn-brand w-full mt-6 rounded-xl py-3 font-semibold disabled:opacity-60">
                {loading ? "Sending…" : "Send reset code"}
              </button>
              <p className="mt-5 text-sm text-center" style={{ color: "var(--muted)" }}>
                Remembered it?{" "}
                <Link to="/sign-in" className="font-semibold" style={{ color: "var(--brand)" }}>Sign in</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={doReset} className="w-full max-w-sm" data-testid="forgot-reset-form">
              <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Enter your code</h1>
              <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
                We sent a 6-digit code to <span style={{ color: "var(--ink)" }}>{email}</span>. It expires in 15 minutes.
              </p>
              <label className="block mt-6 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                6-digit code
                <input
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="reset-code"
                  className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none tracking-[0.4em] text-center text-lg"
                  style={inputStyle}
                  placeholder="------"
                />
              </label>
              <label className="block mt-4 text-sm font-semibold" style={{ color: "var(--ink)" }}>
                New password
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password"
                  className="mt-1.5 w-full rounded-xl px-4 py-3 outline-none"
                  style={inputStyle}
                  placeholder="At least 8 characters"
                />
              </label>
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="btn-brand w-full mt-6 rounded-xl py-3 font-semibold disabled:opacity-60">
                {loading ? "Updating…" : "Set new password"}
              </button>
              <button type="button" onClick={() => setStep("request")} className="mt-3 w-full text-sm" style={{ color: "var(--muted)" }}>
                Didn&apos;t get it? Resend to a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
